import { useAuth } from "../contexts/AuthContext";
import { fmt, fmt1, fmtPct1 } from "../lib/format";
import type { RetailMonth, RetailStore, StoreListRow } from "../lib/types";
import { ChecksAllLine } from "./EmployeeScreen";
import { toneOfRatio, TONE_BG } from "../lib/tone";
import { netNoteKey } from "../lib/retail";

interface Props {
  retail: RetailMonth;
  /** null — все магазины; иначе ключ магазина */
  store: string | null;
  final: boolean;
  /** администратор магазина: список берётся из retail.store_rows, строки не кликабельны */
  storeAdmin?: boolean;
  onSelectPerson: (personId: string) => void;
}

type ListRow = StoreListRow & { person_id?: string };

function toListRow(r: { away_store?: string | null } & Omit<ListRow, "away" | "display_name"> & { display_name?: string | null }): ListRow {
  return { ...r, display_name: r.display_name ?? null, away: r.away_store != null };
}

/** Режимы владельца «все магазины» и «магазин»: сводка, карточки магазинов, список сотрудниц. */
export function StoresScreen({ retail, store, final, storeAdmin = false, onSelectPerson }: Props) {
  const { t } = useAuth();
  const stores = store ? retail.stores.filter(s => s.store === store) : retail.stores;
  const source: ListRow[] = storeAdmin ? (retail.store_rows ?? []) : retail.rows.map(toListRow);
  const rows = [...source]
    .filter(r => !store || r.store === store)
    .sort((a, b) => (a.display_name ?? "").localeCompare(b.display_name ?? "", "ru"));
  const totalNet = rows.reduce((a, r) => a + r.net, 0);
  const totalFact = stores.reduce((a, s) => a + s.fact, 0);
  const totalPlan = stores.reduce((a, s) => a + s.plan, 0);

  return (
    <section className="p-4 space-y-3">
      {!store && (
        <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-2xl p-4 border border-teal-100">
          <div className="flex items-center justify-between mb-1">
            <div className="text-xs text-teal-700">{t("stores_both")}</div>
            {!final && <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">{t("prelim_badge")}</span>}
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-3xl font-bold text-teal-900 tabular-nums">{fmt(totalFact)}</span>
            <span className="text-sm text-teal-700 tabular-nums">грн {t("store_from", { plan: `${fmt(totalPlan)} грн` })}</span>
          </div>
          <div className="text-xs text-slate-600 mt-1 tabular-nums">{t("stores_total_note", { net: `${fmt(totalNet)} грн`, n: rows.length })}</div>
          <ChecksAllLine parts={stores} />
        </div>
      )}

      {stores.map(s => <StoreCard key={s.store} s={s} />)}

      <div className="bg-white rounded-2xl px-4 py-2 border border-slate-200">
        <div className="text-xs uppercase tracking-wide text-slate-500 pt-2 pb-1">
          {t("employees_title")}{store ? ` · ${stores[0]?.label ?? store}` : ""}
        </div>
        {rows.map((r, i) => <EmployeeRow key={r.person_id ?? `${r.display_name}-${i}`} r={r} final={final} onClick={storeAdmin || !r.person_id ? null : () => onSelectPerson(r.person_id!)} />)}
        {/* Итог к выплате: по магазину — «Всего к выплате по магазину», по всем — «Итого к выплате»; статус месяца рядом (решение владельца 28.10.2026) */}
        <div className="flex items-center justify-between text-sm py-2.5 border-t-2 border-slate-200">
          <span className="text-slate-700 font-medium">
            {t(store ? "store_total_net" : "total_net")}
            <span className={`ml-2 text-[10px] font-normal px-2 py-0.5 rounded-full ${final ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>{t(final ? "final_badge" : "prelim_badge")}</span>
          </span>
          <span className="font-bold text-slate-900 tabular-nums">{fmt(totalNet)} грн</span>
        </div>
      </div>
    </section>
  );
}

function StoreCard({ s }: { s: RetailStore }) {
  const { t } = useAuth();
  const ratio = s.plan > 0 ? s.fact / s.plan : 0;
  return (
    <div className="bg-slate-50 rounded-2xl p-4 border border-slate-200">
      <div className="flex items-start justify-between mb-1">
        <div className="text-xs uppercase tracking-wide text-slate-500">{t("store_title", { label: s.label })}</div>
        <div className="text-xs text-slate-500 tabular-nums">{t("store_of_plan", { pct: fmtPct1(ratio * 100) })}</div>
      </div>
      <div className="flex items-baseline gap-1">
        <span className="text-xl font-bold text-slate-900 tabular-nums">{fmt(s.fact)} грн</span>
        <span className="text-xs text-slate-500 tabular-nums">{t("store_from", { plan: `${fmt(s.plan)} грн` })}</span>
      </div>
      <div className="h-1.5 bg-slate-200 rounded-full mt-2 overflow-hidden">
        <div className={`h-full ${TONE_BG[toneOfRatio(ratio)]}`} style={{ width: `${Math.min(ratio, 1) * 100}%` }} />
      </div>
      <div className="grid grid-cols-3 gap-2 text-center mt-3">
        <div><div className="text-sm font-bold text-slate-900 tabular-nums">{fmt(s.revenue)}</div><div className="text-[10px] text-slate-500">{t("store_revenue")}</div></div>
        <div><div className="text-sm font-bold text-slate-900 tabular-nums">−{fmt(s.returns)}</div><div className="text-[10px] text-slate-500">{t("store_returns")}</div></div>
        <div><div className="text-sm font-bold text-slate-900 tabular-nums">{s.checks_count}</div><div className="text-[10px] text-slate-500">{t("store_checks")}</div></div>
      </div>
      <div className="text-[10px] text-slate-500 mt-2 tabular-nums">
        {t("store_avg_note", { avg: s.avg_check == null ? "—" : `${fmt(s.avg_check)} грн`, plan: fmt(s.avg_check_plan), n: s.norm_shifts })}
      </div>
      <ChecksAllLine parts={[s]} />
      {s.revenue_parts && <RevenueParts s={s} p={s.revenue_parts} />}
    </div>
  );
}

/**
 * Из чего сложилась выручка (brazerie-1c п. 16, задача владельца 31.10.2026): деньги, сертификаты по ½ номинала, возвраты.
 * Ничего не пересчитывается: «до возвратов» — stores.revenue (без возвратов, п. 16), итог — stores.fact (идёт в % плана).
 */
function RevenueParts({ s, p }: { s: RetailStore; p: NonNullable<RetailStore["revenue_parts"]> }) {
  const { t } = useAuth();
  const line = (label: string, value: string, key: string) => (
    <div key={key} className="flex justify-between gap-2"><span className="text-slate-500">{label}</span><span className="tabular-nums text-slate-700">{value}</span></div>
  );
  return (
    <div className="mt-3 pt-3 border-t border-slate-200 text-[11px] space-y-1">
      {line(t("rev_cash"), `${fmt(p.cash)} грн`, "cash")}
      {/* терминал и наличные — только когда 1С отдала оба (решение владельца 01.11.2026) */}
      {p.terminal != null && p.notes != null && (
        <div className="pl-3 space-y-1 text-[10px]">
          {line(t("rev_terminal"), `${fmt(p.terminal)} грн`, "term")}
          {line(t("rev_notes"), `${fmt(p.notes)} грн`, "notes")}
        </div>
      )}
      {/* депозит клиенток с 10.2026 входит в выручку (brazerie-1c п. 16): переплата −, оплачено депозитом +; строки только при > 0 */}
      {(p.overpaid ?? 0) > 0 && line(t("rev_overpaid"), `−${fmt(p.overpaid!)} грн`, "over")}
      {(p.deposit_paid ?? 0) > 0 && line(t("rev_deposit_paid"), `+${fmt(p.deposit_paid!)} грн`, "dep")}
      {line(t("rev_cert_sold", { nom: `${fmt(p.cert_sold)} грн` }), `−${fmt(p.cert_sold / 2)} грн`, "sold")}
      {line(t("rev_cert_paid", { nom: `${fmt(p.cert_paid)} грн` }), `+${fmt(p.cert_paid / 2)} грн`, "paid")}
      <div className="flex justify-between gap-2 pt-1 border-t border-slate-200 text-slate-700">
        <span>{t("rev_before_returns")}</span><span className="tabular-nums">{fmt(s.revenue)} грн</span>
      </div>
      {line(t("rev_returns"), `−${fmt(s.returns)} грн`, "ret")}
      <div className="flex justify-between gap-2 pt-1 border-t border-slate-200 font-semibold text-slate-900">
        <span>{t("rev_total")}</span><span className="tabular-nums">{fmt(s.fact)} грн</span>
      </div>
    </div>
  );
}

function EmployeeRow({ r, final, onClick }: { r: ListRow; final: boolean; onClick: (() => void) | null }) {
  const { t } = useAuth();
  const cls = "w-full text-left flex items-center justify-between gap-2 py-2.5 border-b border-slate-100 last:border-0";
  const inner = (
    <>
      <div className="min-w-0">
        <div className="text-sm font-medium text-slate-900 truncate">{r.display_name ?? r.person_id}</div>
        <div className="text-[11px] text-slate-500">
          {r.store_label} · {r.role === "admin" ? t("role_admin") : t("role_pk")}
          {r.data_pending && <span className="text-amber-700"> · {t("data_pending_short")}</span>}
        </div>
        {/* командировочные и админ-надбавка: в предварительном месяце ещё не в нетто (решение владельца 28–29.10.2026) */}
        {netNoteKey(r, final) && <div className="text-[11px] text-amber-700">{t(netNoteKey(r, final)!)}</div>}
      </div>
      <div className="text-right shrink-0">
        <div className="text-sm font-semibold text-slate-900 tabular-nums">{fmt1(r.kpi)} <span className="text-[10px] font-normal text-slate-500">%</span></div>
        <div className="text-[11px] text-teal-700 tabular-nums">{fmt(r.net)} грн</div>
      </div>
    </>
  );
  // Администратор магазина: строки без перехода в карточки сотрудниц (решение владельца 28.10.2026)
  return onClick ? <button onClick={onClick} className={cls}>{inner}</button> : <div className={cls}>{inner}</div>;
}
